import {
  collection, doc, addDoc, getDoc, getDocs,
  query, where, serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { DRXError } from '@/lib/drx-error';
import type {
  IOrderRepository, CreateOrderData, GetOrderData,
} from './interfaces/order.repository.interface';

export class OrderRepository implements IOrderRepository {
  private collectionName = 'orders';

  async createOrder(orderData: CreateOrderData): Promise<GetOrderData> {
    try {
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...orderData,
        createdAt: serverTimestamp(),
      });
      const created = await this.getOrder(docRef.id);
      if (!created) {
        throw new DRXError({
          code: 'ORDER_CREATE_FAILED',
          devMessage: `Order created but could not be retrieved: ${docRef.id}`,
        });
      }
      return created;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'ORDER_CREATE_FAILED',
        devMessage: 'Failed to create order',
        cause: error,
      });
    }
  }

  async getUserOrders(userId: string): Promise<GetOrderData[]> {
    try {
      const q = query(collection(db, this.collectionName), where('userId', '==', userId));
      const snapshot = await getDocs(q);
      const orders = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as GetOrderData));
      return orders.sort((a, b) => {
        const aDate = a.createdAt?.toDate?.() || new Date(0);
        const bDate = b.createdAt?.toDate?.() || new Date(0);
        return bDate.getTime() - aDate.getTime();
      });
    } catch (error) {
      throw new DRXError({
        code: 'ORDER_FETCH_FAILED',
        devMessage: `Failed to fetch orders for user ${userId}`,
        cause: error,
      });
    }
  }

  async getOrderByNumber(orderNumber: string): Promise<GetOrderData | null> {
    try {
      // Try by document ID first
      const orderDoc = await getDoc(doc(db, this.collectionName, orderNumber));
      if (orderDoc.exists()) {
        return { id: orderDoc.id, ...orderDoc.data() } as GetOrderData;
      }
      // Fallback: query by orderNumber field, among the member's own orders
      // (members can read only their own orders).
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', auth.currentUser?.uid ?? ''),
        where('orderNumber', '==', orderNumber),
      );
      const snapshot = await getDocs(q);
      if (snapshot.docs.length > 0) {
        return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as GetOrderData;
      }
      return null;
    } catch (error) {
      throw new DRXError({
        code: 'ORDER_FETCH_FAILED',
        devMessage: `Failed to fetch order by number ${orderNumber}`,
        cause: error,
      });
    }
  }

  async getOrder(orderId: string): Promise<GetOrderData | null> {
    try {
      const docSnap = await getDoc(doc(db, this.collectionName, orderId));
      return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as GetOrderData : null;
    } catch (error) {
      throw new DRXError({
        code: 'ORDER_FETCH_FAILED',
        devMessage: `Failed to fetch order ${orderId}`,
        cause: error,
      });
    }
  }
}
